import { FC, useCallback, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Divider,
  RadixTable,
  TColumn,
} from '@features/ui';
import { StarIcon } from '@features/ui/icons';
import { formatCurrency, formatDate } from '@lib/utils.ts';
import { ApprovePayload, TAwaitingApproval } from '@src/types';
import Pagination from '@features/ui/components/pagination.tsx';
import { AccountChartsDialog } from '@features/admin/users/AccountChartsDialog.tsx';
import { AccountStatusCell } from '@features/admin/components/AccountStatusCell';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';

interface AwaitingApprovalProps {
  data: TAwaitingApproval[];
  currentPage: number;
  totalPages: number;
  selectedItems: ApprovePayload[];
  selectAll: boolean;
  onChangePage: (page: number) => void;
  onBlock?: (payload: ApprovePayload) => void;
  onApprove?: (payload: ApprovePayload) => void;
  onDeny?: (payload: ApprovePayload) => void;
  onSelectItem: (item: ApprovePayload) => void;
  onSelectAll: (items: ApprovePayload[]) => void;
}
export const AwaitingApprovalTable: FC<AwaitingApprovalProps> = ({
  data,
  onApprove,
  totalPages,
  currentPage,
  selectedItems,
  selectAll,
  onChangePage,
  onDeny,
  onBlock,
  onSelectItem,
  onSelectAll,
}) => {
  const [chartsOpen, setChartsOpen] = useState(false);
  const [chartAccountId, setChartAccountId] = useState<number | null>(null);
  const [chartAccountName, setChartAccountName] = useState<string>('');

  const handleSelectItem = useCallback(
    (item?: TAwaitingApproval) => {
      if (!item || !item.leeloo_user) return;
      const newSelectedItem = {
        leeloo_account_id: item.id,
        leeloo_user_id: item.leeloo_user.id,
      };

      onSelectItem(newSelectedItem);
    },
    [onSelectItem]
  );

  const handleSelectAll = useCallback(() => {
    const newSelectedItems = !selectAll
      ? data
          .filter((item) => item.leeloo_user)
          .map((item) => ({
            leeloo_account_id: item.id,
            leeloo_user_id: item.leeloo_user!.id,
          }))
      : [];

    onSelectAll(newSelectedItems);
  }, [data, onSelectAll, selectAll]);

  const handleOpenCharts = useCallback((item?: TAwaitingApproval) => {
    if (!item?.id) return;
    setChartAccountId(item.id);
    setChartAccountName(item.rithmic_account_name ?? '');
    setChartsOpen(true);
  }, []);

  const isItemSelected = useCallback(
    (itemId?: number) => {
      return selectedItems.some(
        (selected) => selected.leeloo_account_id === itemId
      );
    },
    [selectedItems]
  );

  const columns = useMemo(
    (): TColumn<TAwaitingApproval>[] => [
      {
        render: (item) => (
          <div className={`flex items-center gap-x-2.5`}>
            <Checkbox
              checked={isItemSelected(item?.id)}
              className={'border-[white] border-[1px]'}
              id={`user-${item?.id}`}
              onCheckedChange={() => handleSelectItem(item)}
            />
            <div className={'flex flex-col gap-1'}>
              <p className={'text-blue'}>{item?.leeloo_user?.username ?? ''}</p>
              <p>
                {item?.leeloo_user?.first_name ?? ''}{' '}
                {item?.leeloo_user?.last_name ?? ''}
              </p>
            </div>
          </div>
        ),
        title: (
          <div className={'flex items-center gap-x-2.5'}>
            <Checkbox
              checked={selectAll}
              className={'border-[white] border-[1px]'}
              id="user"
              onCheckedChange={handleSelectAll}
            />
            <p>User</p>
          </div>
        ),
      },
      {
        title: 'Account ID/ Package',
        render: (item) => (
          <div className={'flex flex-col gap-1'}>
            <p className={'text-blue'}>{item?.rithmic_account_name ?? ''}</p>
            <p>{item?.product?.title ?? ''}</p>
          </div>
        ),
      },
      {
        title: (
          <div className={'flex flex-col gap-1 min-w-[100px]'}>
            <p className={'text-blue'}>Trader Paid</p>
            <p>Leeloo Paid</p>
          </div>
        ),
        render: (item) => (
          <div>
            <p className={'text-blue'}>${item?.trader_paid}</p>
            <p>${item?.leeloo_paid}</p>
          </div>
        ),
      },
      {
        title: 'Country',
        render: (item) => <p>{item?.leeloo_user?.country ?? ''}</p>,
      },
      {
        title: 'Trading',
        render: (item) => (
          <button
            className={`relative flex h-[30px] min-w-[30px] px-1 bg-[#B6D72C] rounded-[2px] items-center w-fit justify-center cursor-pointer hover:bg-[#a0c002] transition-colors`}
            onClick={() => handleOpenCharts(item)}
          >
            {Number(item?.extra_trading_days ?? 0) +
              Number(item?.trading_days ?? 0) >
              Number(item?.trading_days ?? 0) && (
              <StarIcon
                className="absolute -top-1 -right-1"
                color="#000000"
                size={10}
                title="Has extra trading days"
              />
            )}
            {Number(item?.trading_days) + Number(item?.extra_trading_days)}
          </button>
        ),
      },
      {
        title: 'Active PA',
        render: (item) => <p>{item?.leeloo_user?.active_pa ?? 0}</p>,
      },
      {
        title: 'Balance',
        render: (item) => (
          <div>
            <p>${formatCurrency(Number(item?.account_balance ?? 0), 2)}</p>
            <p>
              {item?.account_balance_date
                ? formatDate(item.account_balance_date)
                : ''}
            </p>
          </div>
        ),
      },
      {
        title: 'Status',
        render: (item) => (
          <AccountStatusCell
            qualifyingAccountName={item?.qualifying_account_name}
            qualifyingStatus={item?.qualifying_status}
            qualifyingStatusDetail={item?.qualifying_status_detail}
          />
        ),
      },
      {
        title: 'Flagged',
        className: 'text-center',
        render: (item) => <p>{item?.leeloo_user?.is_flagged ? 'Yes' : 'No'}</p>,
      },
      {
        title: 'Action',
        render: (item) => (
          <div className={'flex gap-x-[15px]'}>
            {canAccess(UserRolePermission.APPROVE_EVAL) && (
              <Button
                size={'sm'}
                variant={'lime'}
                onClick={() =>
                  item?.id &&
                  item.leeloo_user &&
                  onApprove?.({
                    leeloo_account_id: item.id,
                    leeloo_user_id: item.leeloo_user.id,
                  })
                }
              >
                Approve
              </Button>
            )}
            {canAccess(UserRolePermission.DENY_EVAL) && (
              <Button
                className={'bg-[#C13839]'}
                size={'sm'}
                onClick={() =>
                  item?.id &&
                  item.leeloo_user &&
                  onDeny?.({
                    leeloo_account_id: item.id,
                    leeloo_user_id: item.leeloo_user.id,
                  })
                }
              >
                Deny
              </Button>
            )}
            {canAccess(UserRolePermission.BLOCK_EVAL) && (
              <Button
                size={'sm'}
                variant={'black'}
                onClick={() =>
                  item?.id &&
                  item.leeloo_user &&
                  onBlock?.({
                    leeloo_account_id: item.id,
                    leeloo_user_id: item.leeloo_user.id,
                  })
                }
              >
                Block
              </Button>
            )}
          </div>
        ),
      },
    ],
    [
      handleSelectAll,
      handleSelectItem,
      handleOpenCharts,
      isItemSelected,
      onApprove,
      onBlock,
      onDeny,
      selectAll,
    ]
  );

  return (
    <>
      <Card className={'flex items-end'}>
        <RadixTable columns={columns} data={data} />
        <Divider className={'w-full mb-7.5'} />
        <Pagination
          current={currentPage}
          total={totalPages}
          onChange={onChangePage}
        />
      </Card>

      {/* Charts Popup */}
      <AccountChartsDialog
        accountId={chartAccountId}
        accountName={chartAccountName}
        open={chartsOpen}
        onOpenChange={setChartsOpen}
      />
    </>
  );
};
